// src/pages/NoticiaGenerica.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Spinner } from "react-bootstrap";
import "./noticia-generica.css";
import NavbarFacach from "../components/NavbarFacach";
import FooterFacach from "../components/FooterFacach";
import SocialBar from "../components/SocialBar";
import NewsCoverflowImages from "../components/NewsCoverflowImages";




const API_URL  = "https://facach.coop.hn/api";
const API_BASE = "https://facach.coop.hn"; // ← base para resolver rutas relativas




// ─── Helper: convierte rutas relativas en URL absoluta del backend ──────────
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
};




// ─── Placeholder si no hay imagen ────────────────────────────────────────────
const PlaceholderThumb = ({ w = 260, h = 170 }) => (
  <div
    className="placeholder-thumb d-flex align-items-center justify-content-center bg-light rounded"
    style={{ width: w, height: h }}
    aria-hidden="true"
  >
    <svg width="56" height="56" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#bdbdbd"
        d="M19 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m0 16H5V5h14zM8 13l2.5 3.01L13 13l3 4H8zM9 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4"
      />
    </svg>
  </div>
);




// ─────────────────────────────────────────────────────────────────────────────
const NoticiaGenerica = () => {
  const { id } = useParams();




  const [noticia, setNoticia]     = useState(null);
  const [recientes, setRecientes] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);




  // Scroll al tope cada vez que cambia el id
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);




  // Fetch de la noticia
  useEffect(() => {
    const fetchNoticia = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetch(`${API_URL}/news/${id}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.ok) {
          setNoticia(json.data.noticia);
          setRecientes(json.data.recientes);
        }
      } catch (err) {
        console.error("Error cargando noticia:", err.message);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };




    fetchNoticia();
  }, [id]);




  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }




  // ── Galería de imágenes para el coverflow (URLs normalizadas) ─────────────
  const coverflowItems = noticia?.gallery?.length > 0
    ? noticia.gallery.map((g, i) => ({
        id: `gallery-${i}`,
        src: resolveImageUrl(g.image_url),
        alt: g.alt_text || g.caption || `Imagen ${i + 1}`,
      }))
    : [];




  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <NavbarFacach />
      <Container className="py-4">
        <div className="news-detail-grid">




          {/* ── Contenido principal ────────────────────────────────────────── */}
          <main>
            {/* Imagen destacada */}
            <div className="hero-box mb-3">
              {noticia?.image_url ? (
                <img
                  src={resolveImageUrl(noticia.image_url)}
                  alt={noticia.image_alt || noticia.title}
                  className="img-fluid rounded w-100"
                  style={{ maxHeight: 420, objectFit: "cover" }}
                  loading="lazy"
                />
              ) : (
                <PlaceholderThumb w="100%" h={320} />
              )}
            </div>




            {/* Título */}
            <h3 className="news-detail-title mb-2">
              {notFound ? "Noticia no encontrada" : noticia?.title}
            </h3>




            {/* Cuerpo */}
            {!notFound && noticia ? (
              <>
                <p className="text-muted mb-3">
                  {new Date(noticia.publish_date).toLocaleDateString("es-HN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>




                {/* Renderiza el HTML del CMS directamente */}
                <div
                  className="news-body"
                  dangerouslySetInnerHTML={{ __html: noticia.description_html }}
                />
              </>
            ) : (
              <div className="lorem-lines">
                <span className="line long" />
                <span className="line long" />
                <span className="line mid" />
                <span className="line long" />
                <span className="line short" />
              </div>
            )}




            <div className="mt-4">
              <Link to="/noticias" className="btn btn-outline-secondary btn-sm">
                 Volver a noticias
              </Link>
            </div>
          </main>




          {/* Separador vertical (decorativo) */}
          <div className="divider" aria-hidden="true" />




          {/* ── Aside: Noticias recientes ───────────────────────────────────── */}
          <aside>
            <h6 className="text-center mb-3">Noticias Recientes</h6>
            <ul className="recents-list list-unstyled">
              {recientes.length > 0 ? (
                recientes.map((n) => (
                  <li
                    key={n.id}
                    className="recent-item d-flex align-items-center gap-2 mb-2"
                  >
                    {/* Miniatura */}
                    {n.image_url ? (
                      <img
                        src={resolveImageUrl(n.image_url)}
                        alt={n.title}
                        style={{ width: 72, height: 44, objectFit: "cover", borderRadius: 4 }}
                      />
                    ) : (
                      <div
                        className="placeholder-thumb d-flex align-items-center justify-content-center bg-light rounded"
                        style={{ width: 72, height: 44 }}
                        aria-hidden="true"
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="#bdbdbd" d="M19 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m0 16H5V5h14zM8 13l2.5 3.01L13 13l3 4H8zM9 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4" />
                        </svg>
                      </div>
                    )}
                    <Link
                      to={`/noticias/${n.id}`}
                      className="recent-title text-decoration-none text-dark"
                    >
                      {n.title}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-muted text-center small">No hay noticias recientes</li>
              )}
            </ul>
          </aside>
        </div>




        <SocialBar />
        <br /><br />




        {/* Galería coverflow solo si hay imágenes */}
        {coverflowItems.length > 0 && (
          <div className="mb-3">
            <NewsCoverflowImages items={coverflowItems} />
          </div>
        )}
      </Container>
      <br /><br /><br />
      <FooterFacach />
    </>
  );
};




export default NoticiaGenerica;



